import { type AnyStateMachine, createActor, type SnapshotFrom } from "xstate";

// The package root (@upclaim/machines) fails this app's stricter tsc; import
// the litigation-specific entrypoint directly instead. The machines package
// resolves its own xstate copy, hence the cast to this app's AnyStateMachine.
import { getMachine as getAirLitigationMachine } from "@upclaim/machines/air-litigation";

export type ClaimMachineSnapshot = SnapshotFrom<AnyStateMachine>;

/** Mirrors getMachine from @upclaim/machines. */
function getMachine(litigationName: string, versionNumber: number): AnyStateMachine | undefined {
  if (litigationName === "AIR_LITIGATION") {
    return getAirLitigationMachine(versionNumber) as unknown as AnyStateMachine | undefined;
  }
  return undefined;
}

export interface ReplayClaimEventsInput {
  litigationName: string;
  versionNumber: number;
  /** Event display names, in chronological order (claimEvents[].eventType.displayName). */
  eventNames: string[];
}

/**
 * Replays the claim events through the litigation machine to reconstruct its
 * current state — mirrors ClaimStore.retrieveClaim (Vue).
 */
export function replayClaimEvents({
  litigationName,
  versionNumber,
  eventNames,
}: ReplayClaimEventsInput): ClaimMachineSnapshot | null {
  const machine = getMachine(litigationName, versionNumber);
  if (!machine) {
    return null;
  }

  const actor = createActor(machine).start();
  for (const event of eventNames) {
    actor.send({ type: event });
  }

  const snapshot = actor.getSnapshot();
  actor.stop();
  return snapshot;
}

export interface PreviewNextLegalStepInput {
  litigationName: string;
  versionNumber: number;
  /** Current machine snapshot (from replayClaimEvents). */
  snapshot: ClaimMachineSnapshot;
  /** Machine event to simulate. */
  event: string;
}

/**
 * Simulates sending `event` from the current snapshot and returns the
 * resulting LEGAL_STEP — mirrors SendEventModal.nextLegalStep (Vue).
 */
export function previewNextLegalStep({
  litigationName,
  versionNumber,
  snapshot,
  event,
}: PreviewNextLegalStepInput): string | undefined {
  const machine = getMachine(litigationName, versionNumber);
  if (!machine) {
    return undefined;
  }

  const actor = createActor(machine, { snapshot: snapshot as never }).start();
  actor.send({ type: event });
  const afterState = actor.getSnapshot();
  actor.stop();

  return (afterState.context as { LEGAL_STEP?: string } | undefined)?.LEGAL_STEP;
}

interface SnapshotWithNodes {
  _nodes?: { transitions?: Map<string, unknown> }[];
}

/**
 * Events accepted from the current state — mirrors TopMenu.machineStateActions
 * (Vue). "Abandon" is excluded there at the call site, not here.
 */
export function getAvailableEvents(snapshot: ClaimMachineSnapshot | null): string[] {
  if (!snapshot) {
    return [];
  }

  const nodes = (snapshot as unknown as SnapshotWithNodes)._nodes ?? [];
  return Array.from(new Set(nodes.flatMap((node) => Array.from(node.transitions?.keys() ?? []))));
}
